/**
 * Line items table editor for InvoiceManager.
 *
 * Lines are grouped by section_label (e.g. "Roller Bird × 3", "Eagle × 2").
 * Each section has an editable header (rename + multiplier) and a per-section
 * Add button. The bottom of the card carries an "Add new section" control and
 * the existing Reset button. Flat invoices (no section labels on any row)
 * render without a header so legacy data looks identical to before.
 *
 * Per-room convention: row line totals are stored per room. The section
 * subtotal shown in the header is sum(line_total) × section_quantity. The
 * editor's overall subtotal in InvoiceManager.tsx multiplies the same way so
 * the preview matches what the backend writes on save.
 */

import { Fragment, useState } from 'react';
import styles from './invoiceManagerStyles';

interface LineItem {
  name?: string;
  quantity?: number | string;
  rate?: number | string;
  discount?: number | string;
  section_label?: string | null;
  section_quantity?: number;
  [key: string]: unknown;
}

export interface LineItemsEditorProps {
  lineItems: LineItem[];
  formatCurrency: (amount: number) => string;
  onUpdateLineItem: (index: number, field: string, value: string) => void;
  onAddNewLine: (section?: { label: string | null; quantity: number }) => void;
  onResetLineItems: () => void;
  onRemoveLine: (index: number) => void;
  calculateLineTotal: (item: LineItem) => number;
  onRenameSection: (currentLabel: string | null, nextLabel: string | null) => void;
  onUpdateSectionQuantity: (label: string | null, quantity: number) => void;
  onAddNewSection: (label: string, quantity: number) => void;
}

interface IndexedItem {
  item: LineItem;
  idx: number;
}

interface SectionGroup {
  label: string | null;
  quantity: number;
  rows: IndexedItem[];
}

function buildSections(items: LineItem[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  const byLabel = new Map<string, SectionGroup>();
  items.forEach((item, idx) => {
    const label = item.section_label ?? null;
    const key = label ?? '__flat__';
    let group = byLabel.get(key);
    if (!group) {
      group = {
        label,
        quantity: Math.max(1, Number(item.section_quantity) || 1),
        rows: [],
      };
      byLabel.set(key, group);
      groups.push(group);
    }
    group.rows.push({ item, idx });
  });
  return groups;
}

export default function LineItemsEditor({
  lineItems, formatCurrency, onUpdateLineItem, onAddNewLine, onResetLineItems, onRemoveLine, calculateLineTotal,
  onRenameSection, onUpdateSectionQuantity, onAddNewSection,
}: LineItemsEditorProps) {
  const sections = buildSections(lineItems);
  const [newSectionLabel, setNewSectionLabel] = useState('');
  const [newSectionQty, setNewSectionQty] = useState(1);
  const [showNewSection, setShowNewSection] = useState(false);

  const headerCellStyle: React.CSSProperties = {
    background: 'var(--color-bg)',
    padding: '0.5rem 0.75rem',
    borderTop: '2px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
  };

  const submitNewSection = () => {
    if (!newSectionLabel.trim()) return;
    onAddNewSection(newSectionLabel.trim(), newSectionQty);
    setNewSectionLabel('');
    setNewSectionQty(1);
    setShowNewSection(false);
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Line Items</h2>
      <p className="text-sm mb-md" style={{ color: 'var(--color-text-light)' }}>
        Discounts are VAT-inclusive. Per-room quantities, multiplied by the section &times; figure on save.
      </p>
      <div style={styles.tableWrapper}>
        <table className="w-full" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={styles.th}>Item Description</th>
              <th className="text-center" style={{ ...styles.th, width: '80px' }}>Qty</th>
              <th className="text-right" style={{ ...styles.th, width: '120px' }}>Rate</th>
              <th className="text-right" style={{ ...styles.th, width: '100px' }}>Discount</th>
              <th className="text-right" style={{ ...styles.th, width: '120px' }}>Total</th>
              <th className="text-center" style={{ ...styles.th, width: '60px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section, sIdx) => {
              const sectionLineSum = section.rows.reduce(
                (sum, r) => sum + calculateLineTotal(r.item),
                0,
              );
              const sectionTotal = sectionLineSum * section.quantity;
              const groupKey = section.label ?? `__flat__${sIdx}`;
              return (
                <Fragment key={groupKey}>
                  {section.label !== null && (
                    <tr>
                      <td colSpan={6} style={headerCellStyle}>
                        <div className="flex items-center flex-wrap gap-sm">
                          <input
                            type="text"
                            value={section.label}
                            onChange={(e) => onRenameSection(section.label, e.target.value || null)}
                            style={{
                              ...styles.tableInput,
                              fontWeight: 600,
                              minWidth: '200px',
                              flex: 1,
                            }}
                            placeholder="Section name"
                          />
                          <span style={{ color: 'var(--color-text-light)' }}>&times;</span>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={section.quantity}
                            onChange={(e) => onUpdateSectionQuantity(section.label, parseInt(e.target.value, 10) || 1)}
                            style={{ ...styles.tableInput, width: '70px', textAlign: 'center' }}
                          />
                          <span style={{ marginLeft: 'auto', fontWeight: 600 }}>
                            {formatCurrency(sectionTotal)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {section.rows.map(({ item, idx }) => (
                    <tr key={idx} style={styles.tableRow}>
                      <td style={styles.td}>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => onUpdateLineItem(idx, 'name', e.target.value)}
                          style={styles.tableInput}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => onUpdateLineItem(idx, 'quantity', e.target.value)}
                          className="text-center"
                          style={{ ...styles.tableInput, width: '60px' }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          value={item.rate || ''}
                          onChange={(e) => onUpdateLineItem(idx, 'rate', e.target.value)}
                          className="text-right"
                          style={{ ...styles.tableInput, width: '100px' }}
                          step="0.01"
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          value={item.discount || ''}
                          onChange={(e) => onUpdateLineItem(idx, 'discount', e.target.value)}
                          className="text-right"
                          style={{ ...styles.tableInput, width: '80px' }}
                          step="0.01"
                        />
                      </td>
                      <td className="text-right font-medium" style={styles.td}>
                        {formatCurrency(calculateLineTotal(item))}
                      </td>
                      <td className="text-center" style={styles.td}>
                        <button onClick={() => onRemoveLine(idx)} style={styles.removeBtn}>
                          &times;
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={6} style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                      <button
                        onClick={() => onAddNewLine({ label: section.label, quantity: section.quantity })}
                        style={{ ...styles.smallBtn, marginTop: 0 }}
                      >
                        + Add line {section.label ? `to ${section.label}` : ''}
                      </button>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNewSection ? (
        <div className="flex gap-md mt-md items-center flex-wrap">
          <input
            type="text"
            value={newSectionLabel}
            onChange={(e) => setNewSectionLabel(e.target.value)}
            placeholder="Section name (e.g. Travel)"
            style={{ ...styles.input, flex: 1, minWidth: '200px' }}
            autoFocus
          />
          <span style={{ color: 'var(--color-text-light)' }}>&times;</span>
          <input
            type="number"
            min={1}
            step={1}
            value={newSectionQty}
            onChange={(e) => setNewSectionQty(parseInt(e.target.value, 10) || 1)}
            style={{ ...styles.input, width: '90px', textAlign: 'center' }}
          />
          <button onClick={submitNewSection} style={styles.greenBtn}>Add</button>
          <button onClick={() => { setShowNewSection(false); setNewSectionLabel(''); setNewSectionQty(1); }} style={styles.grayBtn}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex gap-md mt-md">
          <button onClick={() => setShowNewSection(true)} style={styles.greenBtn}>+ Add new section</button>
          <button onClick={onResetLineItems} style={styles.grayBtn}>Reset to Original</button>
        </div>
      )}
    </div>
  );
}
